import { useCallback } from 'react';
import { useStore } from 'react-redux';
import { v4 as uuidv4 } from 'uuid';
import { useAppDispatch, useAppSelector } from '../../app/hooks';
import type { RootState } from '../../app/store';
import {
  assistAnswerSent,
  isAssistCardActive,
} from '../../redux/features/assistRequest/assistRequest.slice';
import { selectConfig } from '../../redux/features/config/config.selector';
import { selectUserProfile } from '../../selectors/user';
import { buildAssistAnswerEnvelope } from '../../utils/assistRequest/answer';
import { deliverAssistAnswer } from '../../utils/assistRequest/answerTransport';
import { assistClipStore } from '../../utils/assistRequest/hearAndConfirm/clip';
import { AssistAnswerChoice } from '../../utils/assistRequest/types';
import { assistCardLogFields } from '../../utils/assistRequest/logFields';
import logger from '../../utils/logger';

export interface IAssistAnswerArgs {
  answerChoice: AssistAnswerChoice;
  /** The words on the clicked box; null for `noise` / `unclear`. */
  answerText?: string | null;
}

/**
 * HITL-1774 (HITL 8.0.0): answers the card this agent holds.
 *
 * `sendAnswer` is the only writer of `assist_answer`; the card bodies
 * (HITL-1783 on) call it. One click is final: the card ends at once, and the
 * answer is posted to the event API in the background (it never holds the
 * card up). Null when there is no live card to answer.
 */
const useAssistAnswer = () => {
  const dispatch = useAppDispatch();
  const store = useStore<RootState>();
  const config = useAppSelector(selectConfig);
  const userId = useAppSelector(selectUserProfile)?.id;

  const sendAnswer = useCallback(
    ({ answerChoice, answerText = null }: IAssistAnswerArgs) => {
      const held = store.getState().assistRequest.card;
      if (!isAssistCardActive(held) || !userId) return null;
      const answerId = uuidv4();
      const answeredAt = Date.now();
      const envelope = buildAssistAnswerEnvelope({
        card: held,
        userId: String(userId),
        answerId,
        answerChoice,
        answerText,
        answeredAt,
        clipStats: assistClipStore.stats(held.requestId),
      });
      dispatch(
        assistAnswerSent({
          requestId: held.requestId,
          answerId,
          answerChoice,
          answerText,
          answeredAt,
        })
      );
      logger.info({
        message: 'Assist answer sent',
        ...assistCardLogFields(held),
        answerId,
        answerChoice,
        decisionMs: answeredAt - held.receivedAt,
      });
      void deliverAssistAnswer(envelope, {
        eventAPI: config.EVENT_API,
        eventAPIAuth: config.EVENT_API_BASIC_AUTH_PARAM,
      });
      return answerId;
    },
    [config, dispatch, store, userId]
  );

  return { sendAnswer };
};

export default useAssistAnswer;
